import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { FplDialogTitle } from 'components/common'
import { Mode } from 'constants/index'
import {
  CreateAndUpdateCancellationOrLostReasonDtoInput,
  useCreateCancellationOrLostReasonMutation,
  useUpdateCancellationOrLostReasonMutation,
} from 'generated/graphql'

import CancellationOrLostReasonsForm from './CancellationOrLostReasonsForm'
import UpdateCancellationOrLostReasons from './UpdateCancellationOrLostReasons'

interface IProps {
  id: string | null
  openDialog: boolean
  onCloseDialog: () => void
  fplDataGridActionsRef: React.MutableRefObject<any>
}

const CancellationOrLostReasonsDialog = (props: IProps) => {
  const { id, openDialog, onCloseDialog, fplDataGridActionsRef } = props

  const mode = id === null ? Mode.Create : Mode.Update

  const methods = useForm<CreateAndUpdateCancellationOrLostReasonDtoInput>({
    defaultValues: {
      name: '',
      code: '',
    },
  })

  const {
    handleSubmit,
    formState: { isDirty },
  } = methods

  // mutations
  const [createCancellationOrLostReason, { loading: creatingCancellationOrLostReason }] =
    useCreateCancellationOrLostReasonMutation({
      onCompleted: (data) => {
        if (data?.createCancellationOrLostReason) {
          toast.success(
            `Cancellation Reason (${data.createCancellationOrLostReason?.name}) was created with success.`,
          )
          onCloseDialog()
          fplDataGridActionsRef.current.queryRefetchOnTableUpdate()
        }
      },
    })

  const [updateCancellationOrLostReason, { loading: updatingCancellationOrLostReason }] =
    useUpdateCancellationOrLostReasonMutation({
      onCompleted: (data) => {
        if (data?.updateCancellationOrLostReason) {
          toast.success(
            `Cancellation Reason (${data.updateCancellationOrLostReason?.name}) was updated with success.`,
          )
          onCloseDialog()
        }
      },
    })

  const handleSubmitQuoteLostReason: SubmitHandler<
    CreateAndUpdateCancellationOrLostReasonDtoInput
  > = (formData) => {
    if (mode === Mode.Create) {
      createCancellationOrLostReason({
        variables: {
          input: formData,
        },
      })

      return
    }

    updateCancellationOrLostReason({
      variables: {
        input: {
          id,
          ...formData,
        },
      },
    })
  }

  const handleClose = () => {
    onCloseDialog()
  }

  return (
    <Dialog fullWidth scroll='body' open={openDialog} aria-labelledby='cancellation-reason-dialog'>
      <FplDialogTitle id='cancellation-reason-dialog' onClose={handleClose}>
        <Typography paragraph variant='h4'>
          {mode === Mode.Create ? 'Create' : 'Update'} Cancellation Reason
        </Typography>
      </FplDialogTitle>

      <DialogContent>
        <FormProvider {...methods}>
          {mode === Mode.Create ? (
            <CancellationOrLostReasonsForm mode={mode} />
          ) : (
            <UpdateCancellationOrLostReasons id={Number(id)} mode={mode} />
          )}
        </FormProvider>
      </DialogContent>

      <DialogActions> 
        <Button variant='contained' color='grey' onClick={handleClose}>
          Cancel
        </Button>

        <LoadingButton
          variant='contained'
          disabled={!isDirty}
          loading={creatingCancellationOrLostReason || updatingCancellationOrLostReason}
          onClick={handleSubmit(handleSubmitQuoteLostReason)}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default CancellationOrLostReasonsDialog
